import { useId } from 'react';
import TextInput from 'src/modules/shared/components/atoms/inputs/text-input';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TemplatePersonalizationProps } from '../../../types/templates/template-composer.types';
import { isTemplateFieldValid } from '../../../utils/template-composer.util';

/**
 * Edits only approved template parameters, with their original message context.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const TemplatePersonalization = ({
  definition,
  values,
  onChange,
  disabled,
}: TemplatePersonalizationProps) => {
  const { t } = useI18n();
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div>
        <Text weight="medium">{t('whatsappTemplates.personalize')}</Text>
        <Text size="small" color="secondary">
          {t('whatsappTemplates.personalizeHelp')}
        </Text>
      </div>
      {!definition.fields.length && (
        <div className="rounded-xl bg-secondary-50 p-4">
          <Text size="small">{t('whatsappTemplates.noFields')}</Text>
        </div>
      )}
      {definition.fields.map((field) => {
        const value = values[field.key] ?? '';
        const fieldId = `${id}-${field.key}`;
        const invalid = value.length > 0 && !isTemplateFieldValid(field, value);
        return (
          <div key={field.key} className="flex flex-col gap-2">
            <Text as="label" htmlFor={fieldId} size="small" weight="medium">
              {field.section === 'button'
                ? t('whatsappTemplates.linkField', { label: field.buttonLabel })
                : t(`whatsappTemplates.field.${field.section}`, {
                    number: field.position,
                  })}
            </Text>
            <Text
              size="tiny"
              color="secondary"
              className="break-words whitespace-pre-wrap"
              disableAutoLink
            >
              {field.context}
            </Text>
            {field.urlPrefix && (
              <Text
                size="tiny"
                color="secondary"
                className="break-all"
                disableAutoLink
              >
                {t('whatsappTemplates.urlPrefix', { prefix: field.urlPrefix })}
              </Text>
            )}
            <TextInput
              id={fieldId}
              value={value}
              onChange={(event) => onChange(field.key, event.target.value)}
              disabled={disabled}
              placeholder={t(
                field.section === 'button'
                  ? 'whatsappTemplates.suffixPlaceholder'
                  : 'whatsappTemplates.valuePlaceholder'
              )}
              className="!text-base"
              autoComplete="off"
              aria-required
              aria-invalid={invalid}
              aria-describedby={`${fieldId}-help`}
              error={
                invalid
                  ? t(
                      field.section === 'button'
                        ? 'whatsappTemplates.invalidSuffix'
                        : 'whatsappTemplates.invalidField'
                    )
                  : undefined
              }
            />
            <Text id={`${fieldId}-help`} size="tiny" color="secondary">
              {t(
                field.section === 'button'
                  ? 'whatsappTemplates.suffixHelp'
                  : 'whatsappTemplates.required'
              )}
            </Text>
          </div>
        );
      })}
    </div>
  );
};
